import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiVideo, FiMic } from 'react-icons/fi';
import ImageLightbox from '../ImageLightbox';

/**
 * MediaGallery — renders an `attachments[]` array (and a legacy `photoUrl`
 * string for back-compat). Images open in the shared ImageLightbox; videos
 * render with native `<video controls>`; audio with native `<audio controls>`
 * plus an optional duration. Used by mechanic screens and the admin issue view.
 *
 * Props:
 *   attachments — array of { url, type:'image'|'video'|'audio', filename, duration? }
 *   photoUrl    — legacy single image URL (shown if attachments has no image)
 *   className   — extra wrapper classes
 */

// Normalize a URL the same way the legacy admin view does (leading slash).
const normalize = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('/')) return url;
  return `/${url}`;
};

const formatDuration = (secs) => {
  if (secs == null || Number.isNaN(Number(secs))) return null;
  const s = Math.round(Number(secs));
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
};

const MediaGallery = ({ attachments = [], photoUrl = null, className = '' }) => {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);

  const list = Array.isArray(attachments) ? attachments.filter(Boolean) : [];

  // Back-compat: if there's a legacy photoUrl and no image already present,
  // surface it as a synthetic image attachment.
  const hasImage = list.some((a) => a.type === 'image');
  const items =
    photoUrl && !hasImage
      ? [{ url: photoUrl, type: 'image', filename: '' }, ...list]
      : list;

  if (items.length === 0) {
    return (
      <p
        data-testid="media-gallery-empty"
        className={`text-sm text-content-muted ${className}`}
      >
        {t('staff.capture.empty')}
      </p>
    );
  }

  return (
    <>
      <div
        data-testid="media-gallery"
        className={`flex gap-2 flex-wrap ${className}`}
      >
        {items.map((att, idx) => {
          const url = normalize(att.url);
          if (att.type === 'image') {
            return (
              <button
                key={`g-${idx}`}
                type="button"
                data-testid="gallery-image"
                onClick={() => setLightbox(url)}
                className="w-24 h-24 rounded-xl overflow-hidden border border-edge bg-surface-alt focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <img
                  src={url}
                  alt={att.filename || t('jc.issueEvidenceAlt')}
                  className="w-full h-full object-cover"
                />
              </button>
            );
          }
          if (att.type === 'video') {
            return (
              <video
                key={`g-${idx}`}
                data-testid="gallery-video"
                controls
                src={url}
                className="w-40 max-w-full rounded-xl border border-edge bg-black"
              >
                <FiVideo aria-hidden />
              </video>
            );
          }
          if (att.type === 'audio') {
            const dur = formatDuration(att.duration);
            return (
              <div
                key={`g-${idx}`}
                data-testid="gallery-audio"
                className="flex items-center gap-2 rounded-xl border border-edge bg-surface-alt px-3 py-2"
              >
                <FiMic size={18} className="text-content-muted shrink-0" />
                <audio controls src={url} className="h-9 w-full max-w-[240px]" />
                {dur && (
                  <span className="text-xs text-content-muted tabular-nums">{dur}</span>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>

      {lightbox && (
        <ImageLightbox imageUrl={lightbox} onClose={() => setLightbox(null)} />
      )}
    </>
  );
};

export default MediaGallery;
